/**
 * 납품 이력 — 어느 판을 언제 어느 고객사에 납품했나(2026-10-29). 정비 모드(공급사 계정만)의 자리다.
 *
 * 기록은 이 서버의 DB 에 있다(`GET/POST/PATCH /api/v1/admin/deliveries`) — 콘솔 번들에 싣지 않는다. 번들은 모든 고객사 서버에
 * 똑같이 나가므로 거기 실으면 다른 고객사 이름이 파일째 따라간다. 납품일·고객사·판은 남긴 뒤 고치지 않고(이력이다),
 * **메모만** 고친다 — 현장 담당자, 특이 사항, 후속 일정처럼 납품 뒤에 쌓이는 것.
 */
import { useState, type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { DeliveryRecord, SignageOneClient } from '../api/client.js';
import { ErrorNote, Loading, Modal, Panel, relativeTime } from '../components/ui.js';
import { useDirty, useGuardedClose } from '../components/closeGuard.js';
import { DateField } from '../components/DateField.js';
import { useAction, useAsync } from '../hooks.js';
import { formatActor } from './Activities.js';

const MEMO_MAX = 2000;

/** 오늘(이 PC 의 날짜) — 납품일 칸의 처음 값. */
function today(): string {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function DeliveriesScreen({ client }: { client: SignageOneClient }): ReactNode {
  const deliveries = useAsync(() => client.listDeliveries(), [client]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<DeliveryRecord | null>(null);

  const items = deliveries.data?.items ?? [];
  const memoMax = deliveries.data?.memo_max ?? MEMO_MAX;

  return (
    <div className="screen">
      {deliveries.error ? <ErrorNote message={deliveries.error} onDismiss={deliveries.reload} /> : null}
      <Panel
        title={`납품 ${items.length}건`}
        help="고객사에 납품할 때 설치한 서버 패키지와 에이전트의 판입니다. 납품일·고객사·판은 남긴 뒤 고치지 않고, 메모만 고칩니다."
        actions={
          <>
            {/* 납품판 뒤에 무엇이 바뀌었나 — 납품처를 올려 줄 때 새로 들어가는 것(시스템 문서 › 변경 기록). */}
            <Link to="/docs/changelog" className="btn">
              변경 기록
            </Link>
            <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" strokeWidth={2} />
              납품 추가
            </button>
          </>
        }
      >
        {deliveries.loading && !deliveries.data ? (
          <Loading />
        ) : items.length === 0 ? (
          <p className="hint p-4">납품 기록이 없습니다. [납품 추가] 로 남기세요.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>납품일</th>
                  <th>고객사</th>
                  <th>사업장</th>
                  <th>서버 판</th>
                  <th>에이전트 판</th>
                  <th>메모</th>
                </tr>
              </thead>
              <tbody>
                {items.map((entry) => (
                  <tr key={entry.id}>
                    <td className="whitespace-nowrap">{entry.date}</td>
                    <td>{entry.customer}</td>
                    <td>{entry.site || '—'}</td>
                    <td className="font-mono">{entry.server_version}</td>
                    <td className="font-mono">{entry.agent_version}</td>
                    <td className="min-w-[16rem]">
                      <button
                        type="button"
                        className="block w-full rounded px-1 py-0.5 text-left hover:bg-slate-50"
                        title="메모 고치기"
                        onClick={() => setEditing(entry)}
                      >
                        {entry.memo ? (
                          <span className="line-clamp-2 whitespace-pre-line text-slate-700">{entry.memo}</span>
                        ) : (
                          <span className="text-slate-400">메모 추가</span>
                        )}
                      </button>
                      {entry.memo ? (
                        <span className="mt-0.5 block px-1 text-[11.5px] text-slate-400">
                          {formatActor(entry.updated_by).name} · {relativeTime(entry.updated_at)}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {adding ? (
        <AddDeliveryModal
          client={client}
          memoMax={memoMax}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            deliveries.reload();
          }}
        />
      ) : null}
      {editing ? (
        <MemoModal
          client={client}
          delivery={editing}
          memoMax={memoMax}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            deliveries.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function AddDeliveryModal({
  client,
  memoMax,
  onClose,
  onSaved,
}: {
  client: SignageOneClient;
  memoMax: number;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const [form, setForm] = useState({
    date: today(),
    customer: '',
    site: '',
    server_version: '',
    agent_version: '',
    memo: '',
  });
  const action = useAction();
  const close = useGuardedClose(useDirty(form), onClose);
  const field = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (event: { target: { value: string } }) =>
      setForm((current) => ({ ...current, [key]: event.target.value })),
  });

  return (
    <Modal title="납품 추가" onClose={close}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          void action.run(() => client.createDelivery(form), onSaved);
        }}
      >
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        <label className="field">
          <span>납품일</span>
          <DateField
            label="납품일"
            required
            value={form.date}
            onChange={(date) => setForm((current) => ({ ...current, date }))}
          />
        </label>
        <label className="field">
          <span>고객사</span>
          <input className="input" required maxLength={100} placeholder="LG에너지솔루션" {...field('customer')} />
        </label>
        <label className="field">
          <span>사업장</span>
          <input className="input" maxLength={100} placeholder="과천" {...field('site')} />
        </label>
        <label className="field">
          <span>서버 판</span>
          <input
            className="input font-mono"
            required
            maxLength={60}
            placeholder="20261028-0a8bd36"
            {...field('server_version')}
          />
          <small className="hint">설치한 서버 패키지 파일 이름의 판(날짜-커밋)입니다.</small>
        </label>
        <label className="field">
          <span>에이전트 판</span>
          <input className="input font-mono" required maxLength={30} placeholder="1.0.0" {...field('agent_version')} />
        </label>
        <label className="field">
          <span>메모</span>
          <textarea
            className="input"
            rows={4}
            maxLength={memoMax}
            placeholder="현장 담당자, 특이 사항, 후속 일정"
            {...field('memo')}
          />
        </label>
        <p className="hint">납품일·고객사·판은 남긴 뒤 고칠 수 없습니다. 메모는 나중에도 고칠 수 있습니다.</p>
        <div className="form-actions">
          <button type="button" className="btn" onClick={close}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={action.pending}>
            {action.pending ? '남기는 중…' : '남기기'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function MemoModal({
  client,
  delivery,
  memoMax,
  onClose,
  onSaved,
}: {
  client: SignageOneClient;
  delivery: DeliveryRecord;
  memoMax: number;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const [memo, setMemo] = useState(delivery.memo);
  const action = useAction();
  const close = useGuardedClose(useDirty(memo), onClose);

  return (
    <Modal
      title={`메모 · ${delivery.customer}${delivery.site ? ` ${delivery.site}` : ''} (${delivery.date})`}
      onClose={close}
    >
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          void action.run(() => client.updateDeliveryMemo(delivery.id, memo), onSaved);
        }}
      >
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        <p className="hint">
          서버 <span className="font-mono">{delivery.server_version}</span> · 에이전트{' '}
          <span className="font-mono">{delivery.agent_version}</span>
        </p>
        <label className="field">
          <span>메모</span>
          <textarea
            className="input"
            rows={8}
            maxLength={memoMax}
            autoFocus
            value={memo}
            placeholder="현장 담당자, 특이 사항, 후속 일정"
            onChange={(event) => setMemo(event.target.value)}
          />
          <small className="hint">
            {memo.length} / {memoMax}자
          </small>
        </label>
        <div className="form-actions">
          <button type="button" className="btn" onClick={close}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={action.pending}>
            {action.pending ? '저장 중…' : '저장'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
